<template>
  <div class="home mt-2">
    <entity-header
      title="Servicios"
      :show-filters="true"
      :show-export="true"
      @export="getServiciosExcel"
      :show-new="true"
      new-label="Nuevo Servicio"
      new-tooltip="Nuevo Servicio"
      :show-status-filter="true"
      :status.sync="servicio_activo"
      :has-active-filters="hasActiveFilters"
      @status-change="serviciosBuscar"
      @search="serviciosBuscar"
      @toggle-filters="cargarFiltros"
      @new="showEdit({}, 'add')"
    >
      <template #filters>
        <div class="form-group col mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="servicio_select">Buscar por Servicio</label>
          <input
            type="text"
            class="form-control"
            id="servicio_select"
            placeholder="Filtrar por servicio..."
            v-model="servicio_sel"
            @keyup.enter="serviciosBuscar"
            aria-label="Servicio"
          />
        </div>
      </template>
    </entity-header>

    <div class="alert alert-success" role="alert" v-if="success">
      {{ success }}
    </div>
    <div class="alert alert-danger" role="alert" v-if="error">{{ error }}</div>

    <data-table
      v-if="serviciosBusqueda && serviciosBusqueda.data && serviciosBusqueda.data.length"
      :columns="columns"
      :data="serviciosBusqueda.data"
      :order-by-field="orderByField"
      :order-by-direction="orderByDirection"
      id-key="id_servicio"
      actions-width="140"
      @sort="orderByfield"
    >
      <template #cell-integrador="{ item, value }">
        {{ value || getIntegradorName(item.id_integrador) }}
      </template>

      <template #cell-keyword_desc="{ item }">
        {{ item.keyword || '-' }}
        <small class="idEntity" v-if="item.id_keyword">(ID:{{ item.id_keyword }})</small>
      </template>

      <template #cell-producto="{ item }">
        {{ item.producto }}
        <small class="idEntity" v-if="item.id_producto">(ID:{{ item.id_producto }})</small>
      </template>

      <template #cell-numero_corto="{ item }">
        {{ item.numero_corto }}
        <small class="idEntity" v-if="item.id_operador">(ID:{{ item.id_operador }})</small>
      </template>

      <template #actions="{ item }">
        <table-actions
          :item="item"
          active-key="activo"
          :show-edit="false"
          @toggle="showEdit(item, 'toggle')"
        >
          <template #extra>
            <button
              type="button"
              class="btn btn-primary btn-sm mr-1 pt-0 pb-1"
              data-toggle="tooltip"
              data-placement="top"
              title="Atributos del servicio"
              @click="openAtributosModal(item)"
            >
              <small class="bi-bar-chart-steps"></small>
            </button>
          </template>
        </table-actions>
      </template>
    </data-table>

    <empty-state
      :show="Boolean(post && serviciosBusqueda && serviciosBusqueda.data && !serviciosBusqueda.data.length)"
      message="No se encontraron servicios que coincidan con la búsqueda."
    />

    <Paginador
      v-if="serviciosBusqueda && serviciosBusqueda.pages"
      :pages="serviciosBusqueda.pages"
      @selPage="changePage"
    ></Paginador>

    <!-- Modal Formulario de Creación de Servicio -->
    <servicio-form-modal
      :show="showModal && operation === 'add'"
      :operation="operation"
      :servicio="editData"
      :operadores="operadoresBusqueda"
      :productos="productos"
      :keywords="keywordsBusqueda"
      :id-integrador="id_integrador_sel"
      @close="closeModal"
      @saved="getServiciosBusqueda(false)"
      @created="onServicioCreated"
    />

    <!-- Modal Confirmación Toggle (Suspender / Reactivar) -->
    <crud-modal
      :show="showModal && operation === 'toggle'"
      :title="editData && editData.activo == 1 ? 'Suspender Servicio' : 'Reactivar Servicio'"
      :is-toggle="true"
      :show-save="true"
      :save-text="editData && editData.activo == 1 ? 'Suspender' : 'Reactivar'"
      :save-variant="editData && editData.activo == 1 ? 'warning' : 'success'"
      @close="closeModal"
      @save="toggleStatus"
    >
      <div v-if="errors" class="alert alert-danger my-2" role="alert">
        <i class="bi bi-exclamation-triangle me-1"></i>
        {{ typeof errors === 'object' ? (errors.error || errors.message || JSON.stringify(errors)) : errors }}
      </div>
    </crud-modal>

    <!-- Modal Flotante de Atributos Estandarizado -->
    <crud-modal
      :show="showAtributosModal"
      size="xl"
      :title="atributosModalTitle"
      :show-save="false"
      cancel-text="Cerrar"
      @close="closeAtributosModal"
    >
      <Atributos
        v-if="selectedServicioAtributos"
        :propServicio="selectedServicioAtributos.id_servicio"
        :propProducto="selectedServicioAtributos.id_producto"
        :propOperador="selectedServicioAtributos.id_operador"
        :propTipoSuscripcion="selectedServicioAtributos.id_tipo_suscripcion"
        :propIntegrador="selectedServicioAtributos.id_integrador"
        :isModal="true"
      />
    </crud-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import Paginador from "./Paginador.vue";
import EntityHeader from "./utiles/EntityHeader.vue";
import DataTable from "./utiles/DataTable.vue";
import TableActions from "./utiles/TableActions.vue";
import EmptyState from "./utiles/EmptyState.vue";
import CrudModal from "./utiles/CrudModal.vue";
import Atributos from "./Atributos.vue";
import ServicioFormModal from "./servicios/ServicioFormModal.vue";
import { tableSortMixin } from "@/mixins/tableSortMixin";
import { fetchCachedCatalog } from "../../utils/filterCache";
import { startAsyncExport } from "@/utils/exportHelper";

export default {
  name: "Servicios",
  mixins: [tableSortMixin],

  components: {
    DataTable,
    TableActions,
    EmptyState,
    Paginador,
    EntityHeader,
    Atributos,
    CrudModal,
    ServicioFormModal,
  },

  data() {
    return {
      columns: [
        { label: "Id", field: "id_servicio", width: "80" },
        { label: "Integrador", field: "integrador" },
        { label: "Keyword", field: "keyword_desc" },
        { label: "Producto", field: "producto" },
        { label: "Número", field: "numero_corto" },
        { label: "Estado", field: "activo", type: "badge", width: "110" },
      ],
      showAtributosModal: false,
      selectedServicioAtributos: null,

      success: null,
      error: null,
      id_integrador_sel: localStorage.getItem("integrador") || null,
      servicio_sel: null,
      servicio_activo: null,
      serviciosBusqueda: null,
      post: false,
      showModal: false,
      editData: {},
      operation: null,
      page: 1,
      errors: null,

      tipoSuscripciones: [],
      operadoresBusqueda: [],
      productos: [],
      keywordsBusqueda: [],
    };
  },

  created() {
    this.cargarFiltros();
    this.getServiciosBusqueda();
  },

  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("integrador", ["integradores"]),
    atributosModalTitle() {
      if (!this.selectedServicioAtributos) return "Atributos del Servicio";
      const name = this.selectedServicioAtributos.servicio || this.selectedServicioAtributos.alias || `ID: ${this.selectedServicioAtributos.id_servicio}`;
      return `Atributos del Servicio: ${name}`;
    },
    hasActiveFilters() {
      return (
        Boolean(this.servicio_sel) ||
        (this.servicio_activo !== null && this.servicio_activo !== undefined)
      );
    },
  },

  watch: {
    "$store.state.integrador.integrador"(newVal) {
      this.id_integrador_sel = newVal;
      this.cargarFiltros(true);
      this.page = 1;
      this.getServiciosBusqueda(true);
    },
  },

  methods: {
    getIntegradorName(id) {
      if (!id) return "";
      const list = this.integradores && this.integradores.data ? this.integradores.data : (Array.isArray(this.integradores) ? this.integradores : []);
      const found = list.find((i) => String(i.id_integrador || i.id) === String(id));
      return found ? found.integrador : id;
    },
    cargarFiltros(forceRefresh = false) {
      this.id_integrador_sel = localStorage.getItem("integrador") || null;
      return Promise.allSettled([
        this.getTipodeSuscripcinesBusqueda(true, forceRefresh),
        this.getOperadoresBusqueda(true, forceRefresh),
        this.getProductosBusqueda(true, forceRefresh),
        this.getKeywordsBusqueda(true, forceRefresh),
      ]);
    },

    serviciosBuscar() {
      this.changePage(1);
    },

    changePage(page) {
      this.page = page;
      this.getServiciosBusqueda(false);
    },

    orderByfield(field) {
      this.toggleOrder(field);
      this.getServiciosBusqueda(false);
    },

    getServiciosExcel() {
      startAsyncExport("servicio", {
        id_integrador: this.id_integrador_sel,
        order_by: {
          field: this.orderByField,
          order: this.orderByDirection,
        },
      }, "Servicios");
    },

    getServiciosBusqueda(hideResult = true) {
      if (hideResult) this.serviciosBusqueda = null;
      this.post = false;
      this.$store.commit("setLoading", true);
      axios
        .post(process.env.VUE_APP_API_URL + "servicio/busqueda", {
          page: this.page,
          id_integrador: this.id_integrador_sel,
          servicio: this.servicio_sel,
          activo: this.servicio_activo,
          order_by: { field: this.orderByField, order: this.orderByDirection },
        })
        .then((response) => {
          this.serviciosBusqueda = response.data;
        })
        .catch((e) => {
          if (e.response && e.response.status === 429) {
            Swal.fire({
              title: "Error!",
              text: "Espere un momento y vuelva a intentarlo.",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al buscar los servicios",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          this.post = true;
        });
    },

    getTipodeSuscripcinesBusqueda(hideResult = true, forceRefresh = false) {
      if (hideResult) this.tipoSuscripciones = [];
      return fetchCachedCatalog(
        process.env.VUE_APP_API_URL + "tipoSuscripcion/busqueda",
        {
          get_all: true,
          activo: 1,
          order_by: { field: "id_tipo_suscripcion", order: "ASC" },
        },
        forceRefresh
      )
        .then((response) => {
          this.tipoSuscripciones =
            response && response.data ? response.data.data || response.data : [];
        })
        .catch(() => {});
    },

    getTipoSuscripcionDesc(id) {
      if (!id) return "-";
      const list =
        this.tipoSuscripciones && this.tipoSuscripciones.data
          ? this.tipoSuscripciones.data
          : this.tipoSuscripciones || [];
      const found = list.find((t) => (t.id_tipo_suscripcion || t.id) == id);
      if (found) return found.descripcion || found.tipo_suscripcion || found.alias;
      if (id == 20) return "Servicios de SDP";
      if (id == 10) return "Suscripcion periodica 24hs";
      if (id == 1) return "Alta";
      if (id == 2) return "Baja";
      if (id == 3) return "Renovacion";
      if (id == 4) return "Suscribir";
      return "-";
    },

    getOperadoresBusqueda(hideResult = true, forceRefresh = false) {
      if (hideResult) this.operadoresBusqueda = [];
      if (!this.id_integrador_sel || this.id_integrador_sel === "todos") {
        return Promise.resolve();
      }
      return fetchCachedCatalog(
        process.env.VUE_APP_API_URL + "operador/busqueda",
        {
          id_integrador: this.id_integrador_sel,
          get_all: true,
          activo: 1,
          order_by: { field: "numero_corto", order: "ASC" },
        },
        forceRefresh
      )
        .then((response) => {
          const list =
            response && response.data ? response.data.data || response.data : [];
          this.operadoresBusqueda = Array.isArray(list)
            ? [...list].sort(
                (a, b) =>
                  Number(a.numero_corto || a.numero || 0) -
                  Number(b.numero_corto || b.numero || 0)
              )
            : list;
        })
        .catch(() => {});
    },

    getProductosBusqueda(hideResult = true, forceRefresh = false) {
      if (hideResult) this.productos = [];
      if (!this.id_integrador_sel || this.id_integrador_sel === "todos") {
        return Promise.resolve();
      }
      return fetchCachedCatalog(
        process.env.VUE_APP_API_URL + "producto/busqueda",
        {
          id_integrador: this.id_integrador_sel,
          get_all: true,
          activo: 1,
          order_by: { field: "id_producto", order: "ASC" },
        },
        forceRefresh
      )
        .then((response) => {
          this.productos =
            response && response.data ? response.data.data || response.data : [];
        })
        .catch(() => {});
    },

    getKeywordsBusqueda(hideResult = true, forceRefresh = false) {
      if (hideResult) this.keywordsBusqueda = [];
      if (!this.id_integrador_sel || this.id_integrador_sel === "todos") {
        return Promise.resolve();
      }
      return fetchCachedCatalog(
        process.env.VUE_APP_API_URL + "keyword/list",
        {
          id_integrador: this.id_integrador_sel,
          service: true,
        },
        forceRefresh
      )
        .then((response) => {
          this.keywordsBusqueda =
            response && response.data ? response.data.data || response.data : [];
        })
        .catch(() => {});
    },

    openAtributosModal(item) {
      const obj = Object.assign({}, item);
      if (!obj.id_integrador || obj.id_integrador === "todos") {
        if (this.id_integrador_sel && this.id_integrador_sel !== "todos") {
          obj.id_integrador = this.id_integrador_sel;
        } else if (obj.id_producto && this.productos && this.productos.length) {
          const foundProd = this.productos.find(
            (p) => String(p.id_producto || p.id) === String(obj.id_producto)
          );
          if (foundProd && (foundProd.id_integrador || foundProd.id)) {
            obj.id_integrador = foundProd.id_integrador;
          }
        } else if (obj.id_operador && this.operadoresBusqueda && this.operadoresBusqueda.length) {
          const foundOp = this.operadoresBusqueda.find(
            (o) => String(o.id_operador || o.id) === String(obj.id_operador)
          );
          if (foundOp && (foundOp.id_integrador || foundOp.id)) {
            obj.id_integrador = foundOp.id_integrador;
          }
        }
      }
      this.selectedServicioAtributos = obj;
      this.showAtributosModal = true;
    },

    closeAtributosModal() {
      this.showAtributosModal = false;
      this.selectedServicioAtributos = null;
    },

    showEdit(item, operation) {
      this.cargarFiltros(true);
      this.error = null;
      this.errors = null;
      this.editData = Object.assign({}, item);
      this.operation = operation;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.operation = null;
      this.editData = {};
      this.errors = null;
    },

    onServicioCreated(createdSrv) {
      this.closeModal();
      this.getServiciosBusqueda(false);
      this.openAtributosModal(createdSrv);
    },

    toggleStatus() {
      this.errors = null;
      this.$store.commit("setLoading", true);
      const prevActivo = this.editData.activo;
      const targetActivo = 1 - prevActivo;
      const payload = Object.assign({}, this.editData, { activo: targetActivo });
      delete payload.atributos;

      axios
        .post(process.env.VUE_APP_API_URL + "servicio/update", payload)
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data.error || response.data;
          } else {
            this.closeModal();
            Swal.fire({
              title: "Bien!",
              text: targetActivo == 1 ? "Servicio reactivado correctamente" : "Servicio suspendido correctamente",
              icon: "success",
              confirmButtonText: "Aceptar",
            });
            this.getServiciosBusqueda(false);
          }
        })
        .catch((e) => {
          const resData = e.response && e.response.data;
          if (resData && (resData.error || resData.message)) {
            this.errors = resData.error || resData.message;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al actualizar el servicio",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
        });
    },
  },
};
</script>

<style scoped></style>
